import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Building2, ChevronRight, ChevronDown, Copy, XCircle, RotateCcw,
    Search, RefreshCw, Users as UsersIcon, ShieldCheck, ShieldAlert,
    Clock, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
    active: { label: 'Active', className: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: ShieldCheck },
    in_use: { label: 'In use', className: 'bg-blue-100 text-blue-700 border-blue-200', Icon: CheckCircle2 },
    unused: { label: 'Unused', className: 'bg-slate-100 text-slate-600 border-slate-200', Icon: Clock },
    revoked: { label: 'Revoked', className: 'bg-rose-100 text-rose-700 border-rose-200', Icon: XCircle },
    expired: { label: 'Expired', className: 'bg-amber-100 text-amber-700 border-amber-200', Icon: ShieldAlert },
};

function StatusBadge({ status }) {
    const cfg = STATUS_STYLES[status] || STATUS_STYLES.active;
    const Icon = cfg.Icon;
    return (
        <Badge variant="outline" className={`gap-1 ${cfg.className}`}>
            <Icon className="w-3 h-3" />
            {cfg.label}
        </Badge>
    );
}

function CopyButton({ value, label = 'Copy' }) {
    const [copied, setCopied] = useState(false);
    return (
        <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(value);
                setCopied(true);
                toast.success('Copied to clipboard');
                setTimeout(() => setCopied(false), 1500);
            }}
            data-testid={`copy-btn-${value}`}
        >
            <Copy className="w-3.5 h-3.5 mr-1.5" />
            {copied ? 'Copied' : label}
        </Button>
    );
}

export default function OrganizationsManagement({ authToken }) {
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const [usersByCsic, setUsersByCsic] = useState({});
    const [usersLoading, setUsersLoading] = useState({});
    const [query, setQuery] = useState('');

    const headers = { Authorization: `Bearer ${authToken}` };

    const fetchOrgs = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/admin/organizations`, { headers });
            setOrgs(r.data || []);
        } catch (e) {
            console.error(e);
            toast.error('Failed to load organisations');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async (csic) => {
        setUsersLoading((p) => ({ ...p, [csic]: true }));
        try {
            const r = await axios.get(`${API}/admin/organizations/${encodeURIComponent(csic)}/users`, { headers });
            setUsersByCsic((p) => ({ ...p, [csic]: r.data?.users || [] }));
        } catch (e) {
            console.error(e);
            toast.error('Failed to load users for organisation');
        } finally {
            setUsersLoading((p) => ({ ...p, [csic]: false }));
        }
    };

    useEffect(() => {
        if (authToken) fetchOrgs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    const toggle = (csic) => {
        setExpanded((p) => {
            const next = { ...p, [csic]: !p[csic] };
            if (next[csic] && !usersByCsic[csic]) fetchUsers(csic);
            return next;
        });
    };

    const revokeCode = async (codeId, codeLabel, isUsic = false) => {
        if (!window.confirm(`Revoke ${codeLabel}? This will block future logins using this code.`)) return;
        try {
            await axios.put(`${API}/subscription/codes/${codeId}/revoke`, {}, { headers });
            toast.success(`${codeLabel} revoked`);
            await fetchOrgs();
            // refresh affected user list
            const targetCsic = Object.keys(expanded).find((c) => expanded[c]);
            if (isUsic && targetCsic) await fetchUsers(targetCsic);
        } catch (e) {
            toast.error('Failed to revoke');
        }
    };

    const restoreCode = async (codeId, codeLabel, csic) => {
        try {
            await axios.put(`${API}/subscription/codes/${codeId}/restore`, {}, { headers });
            toast.success(`${codeLabel} restored`);
            await fetchOrgs();
            if (csic) await fetchUsers(csic);
        } catch (e) {
            toast.error('Failed to restore');
        }
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return orgs;
        return orgs.filter(
            (o) =>
                (o.organization_name || '').toLowerCase().includes(q) ||
                (o.code || '').toLowerCase().includes(q),
        );
    }, [orgs, query]);

    return (
        <Card data-testid="organizations-card">
            <CardHeader>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-teal-600" />
                            Organisations
                        </CardTitle>
                        <CardDescription>
                            Every CSIC is one organisation. Click a row to see the USICs and users underneath.
                        </CardDescription>
                    </div>
                    <Button variant="outline" onClick={fetchOrgs} disabled={loading} data-testid="refresh-orgs">
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <div className="mb-4 relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by organisation name or CSIC..."
                        className="pl-10"
                        data-testid="org-search"
                    />
                </div>

                <div className="border rounded-lg overflow-hidden">
                    {/* ── Desktop / tablet: full table ── */}
                    <table className="w-full text-sm hidden md:table" data-testid="orgs-table-desktop">
                        <thead className="bg-slate-50 dark:bg-slate-800/50">
                            <tr>
                                <th className="w-10 px-3 py-3"></th>
                                <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Organisation</th>
                                <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">CSIC</th>
                                <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Status</th>
                                <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Users</th>
                                <th className="px-4 py-3 text-right font-medium text-slate-600 dark:text-slate-300">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {loading ? (
                                <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">Loading...</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No organisations yet. Generate a CSIC to create one.</td></tr>
                            ) : (
                                filtered.map((org) => (
                                    <React.Fragment key={org.id}>
                                        <tr
                                            className="hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer"
                                            onClick={() => toggle(org.code)}
                                            data-testid={`org-row-${org.code}`}
                                        >
                                            <td className="px-3 py-3">
                                                {expanded[org.code] ? (
                                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                                ) : (
                                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                                )}
                                            </td>
                                            <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">
                                                {org.organization_name}
                                            </td>
                                            <td className="px-4 py-3">
                                                <code className="bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded text-xs font-mono">
                                                    {org.code}
                                                </code>
                                            </td>
                                            <td className="px-4 py-3"><StatusBadge status={org.status} /></td>
                                            <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <UsersIcon className="w-3.5 h-3.5" />
                                                    {org.active_users}/{org.total_users}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <CopyButton value={org.code} />
                                                    {org.is_active ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-rose-600 hover:text-rose-700"
                                                            onClick={(e) => { e.stopPropagation(); revokeCode(org.id, org.code); }}
                                                            data-testid={`revoke-csic-${org.code}`}
                                                        >
                                                            <XCircle className="w-3.5 h-3.5 mr-1.5" />
                                                            Revoke
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-emerald-600 hover:text-emerald-700"
                                                            onClick={(e) => { e.stopPropagation(); restoreCode(org.id, org.code, org.code); }}
                                                            data-testid={`restore-csic-${org.code}`}
                                                        >
                                                            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                                                            Restore
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {expanded[org.code] && (
                                            <tr className="bg-slate-50/50 dark:bg-slate-800/20">
                                                <td colSpan="6" className="px-6 py-4">
                                                    {usersLoading[org.code] ? (
                                                        <p className="text-sm text-slate-500">Loading users...</p>
                                                    ) : (usersByCsic[org.code] || []).length === 0 ? (
                                                        <p className="text-sm text-slate-500">No USICs generated yet for this organisation.</p>
                                                    ) : (
                                                        <div className="rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden">
                                                            <table className="w-full text-xs">
                                                                <thead className="bg-slate-100 dark:bg-slate-800">
                                                                    <tr>
                                                                        <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">USIC</th>
                                                                        <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Name</th>
                                                                        <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Email</th>
                                                                        <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Role</th>
                                                                        <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Generated</th>
                                                                        <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Status</th>
                                                                        <th className="px-3 py-2 text-right font-medium text-slate-600 dark:text-slate-300">Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                                                    {(usersByCsic[org.code] || []).map((u) => (
                                                                        <tr key={u.id} className="bg-white dark:bg-slate-900/40">
                                                                            <td className="px-3 py-2">
                                                                                <code className="bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded font-mono">{u.code}</code>
                                                                            </td>
                                                                            <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{u.user_name || '—'}</td>
                                                                            <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{u.user_email || '—'}</td>
                                                                            <td className="px-3 py-2 text-slate-600 dark:text-slate-300 capitalize">
                                                                                {u.user_role ? u.user_role.replace(/_/g, ' ') : '—'}
                                                                                {u.specialty ? <span className="text-slate-400 ml-1">({u.specialty})</span> : null}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-slate-500">
                                                                                {u.generated_at ? new Date(u.generated_at).toLocaleDateString() : '—'}
                                                                            </td>
                                                                            <td className="px-3 py-2"><StatusBadge status={u.status} /></td>
                                                                            <td className="px-3 py-2 text-right">
                                                                                <div className="inline-flex items-center gap-1">
                                                                                    <CopyButton value={u.code} />
                                                                                    {u.is_active ? (
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            className="text-rose-600 hover:text-rose-700"
                                                                                            onClick={() => revokeCode(u.id, u.code, true)}
                                                                                            data-testid={`revoke-usic-${u.code}`}
                                                                                        >
                                                                                            <XCircle className="w-3 h-3 mr-1" />
                                                                                            Revoke
                                                                                        </Button>
                                                                                    ) : (
                                                                                        <Button
                                                                                            variant="ghost"
                                                                                            size="sm"
                                                                                            className="text-emerald-600 hover:text-emerald-700"
                                                                                            onClick={() => restoreCode(u.id, u.code, org.code)}
                                                                                            data-testid={`restore-usic-${u.code}`}
                                                                                        >
                                                                                            <RotateCcw className="w-3 h-3 mr-1" />
                                                                                            Restore
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* ── Mobile: stacked cards ── */}
                    <div className="md:hidden divide-y" data-testid="orgs-list-mobile">
                        {loading ? (
                            <div className="px-4 py-8 text-center text-slate-500 text-sm">Loading…</div>
                        ) : filtered.length === 0 ? (
                            <div className="px-4 py-8 text-center text-slate-500 text-sm">No organisations yet. Generate a CSIC to create one.</div>
                        ) : (
                            filtered.map((org) => (
                                <div key={org.id} className="bg-white dark:bg-slate-900/40">
                                    <button
                                        type="button"
                                        onClick={() => toggle(org.code)}
                                        className="w-full flex items-start justify-between gap-2 p-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/40"
                                    >
                                        <div className="min-w-0 flex-1 space-y-1">
                                            <div className="flex items-center gap-2">
                                                {expanded[org.code]
                                                    ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                    : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                }
                                                <div className="font-medium text-slate-800 dark:text-slate-100 truncate text-sm">
                                                    {org.organization_name}
                                                </div>
                                            </div>
                                            <code className="ml-6 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded text-[11px] font-mono inline-block">{org.code}</code>
                                            <div className="ml-6 flex items-center gap-2 text-xs text-slate-500">
                                                <span className="inline-flex items-center gap-1">
                                                    <UsersIcon className="w-3 h-3" />
                                                    {org.active_users}/{org.total_users} users
                                                </span>
                                            </div>
                                        </div>
                                        <StatusBadge status={org.status} />
                                    </button>

                                    <div className="px-3 pb-3 flex flex-wrap gap-2 -mt-1 ml-6">
                                        <CopyButton value={org.code} />
                                        {org.is_active ? (
                                            <Button variant="outline" size="sm" className="h-8 text-rose-600 border-rose-200" onClick={() => revokeCode(org.id, org.code)} data-testid={`revoke-csic-m-${org.code}`}>
                                                <XCircle className="w-3.5 h-3.5 mr-1" />Revoke
                                            </Button>
                                        ) : (
                                            <Button variant="outline" size="sm" className="h-8 text-emerald-600 border-emerald-200" onClick={() => restoreCode(org.id, org.code, org.code)} data-testid={`restore-csic-m-${org.code}`}>
                                                <RotateCcw className="w-3.5 h-3.5 mr-1" />Restore
                                            </Button>
                                        )}
                                    </div>

                                    {expanded[org.code] && (
                                        <div className="bg-slate-50/60 dark:bg-slate-800/20 border-t border-slate-200 dark:border-slate-700 p-3 space-y-2">
                                            {usersLoading[org.code] ? (
                                                <p className="text-xs text-slate-500">Loading users…</p>
                                            ) : (usersByCsic[org.code] || []).length === 0 ? (
                                                <p className="text-xs text-slate-500">No USICs generated yet.</p>
                                            ) : (
                                                (usersByCsic[org.code] || []).map((u) => (
                                                    <div key={u.id} className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-2.5 space-y-1.5">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[10px] font-mono">{u.code}</code>
                                                            <StatusBadge status={u.status} />
                                                        </div>
                                                        <div className="text-xs text-slate-700 dark:text-slate-200 font-medium truncate">{u.user_name || '—'}</div>
                                                        <div className="text-[11px] text-slate-500 truncate">{u.user_email || '—'}</div>
                                                        <div className="text-[11px] text-slate-500 capitalize">
                                                            {u.user_role ? u.user_role.replace(/_/g, ' ') : '—'}
                                                            {u.specialty ? <span className="text-slate-400 ml-1">({u.specialty})</span> : null}
                                                        </div>
                                                        <div className="flex flex-wrap gap-1.5 pt-1">
                                                            <CopyButton value={u.code} />
                                                            {u.is_active ? (
                                                                <Button variant="outline" size="sm" className="h-7 text-rose-600 border-rose-200" onClick={() => revokeCode(u.id, u.code, true)} data-testid={`revoke-usic-m-${u.code}`}>
                                                                    <XCircle className="w-3 h-3 mr-1" />Revoke
                                                                </Button>
                                                            ) : (
                                                                <Button variant="outline" size="sm" className="h-7 text-emerald-600 border-emerald-200" onClick={() => restoreCode(u.id, u.code, org.code)} data-testid={`restore-usic-m-${u.code}`}>
                                                                    <RotateCcw className="w-3 h-3 mr-1" />Restore
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
